






























import Vue from 'vue';
import axios from 'axios';
import PropertySelect from '../components/PropertySelect.vue';

interface HSProp {
    name: string;
    label: string;
}

// PDF field names are sometimes copy-pasted from HTML sources, leaving
// literal entities (&amp;, &apos;, ...) in the name instead of the real
// character - while a previously saved mapping (or a re-exported PDF)
// may have the decoded character instead. Without normalizing both
// sides, the same field looks like two different ones and shows up as
// both an unmapped field and an orphaned mapping.
const HTML_ENTITIES: { [key: string]: string } = {
    amp: '&', apos: "'", quot: '"', lt: '<', gt: '>', nbsp: ' '
};
const decodeHtmlEntities = (value: string): string => {
    if(typeof value !== 'string') return value;
    return value.replace(/&(#\d+|#x[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, entity) => {
        if(entity[0] === '#') {
            const code = entity[1] === 'x' || entity[1] === 'X'
                ? parseInt(entity.slice(2), 16)
                : parseInt(entity.slice(1), 10);
            return isNaN(code) ? match : String.fromCharCode(code);
        }
        return HTML_ENTITIES[entity] ?? match;
    });
};

interface MapperRow {
    formField: string;
    pdfType: string;
    options?: string[];
    selected: string;
    orphaned: boolean;
}

export default Vue.extend({
    name: 'FillerBetaMapper',
    components: { PropertySelect },
    data() {
        return {
            name: '',
            rows: [] as MapperRow[],
            contactHSProps: [] as HSProp[],
            companyHSProps: [] as HSProp[],
            loading: true,
            saving: false,
            loadError: ''
        }
    },
    computed: {
        fileUrl(): string {
            // #navpanes=0 hides the browser PDF viewer's thumbnail/bookmark
            // side panel - just the main page preview, per the request.
            return `${this.$store.state.apiUrl}/filler/template/${encodeURIComponent(this.name)}/file#navpanes=0&toolbar=1`;
        }
    },
    async created() {
        this.name = '' + this.$route.params.name;

        const propsPromise = axios.get(this.$store.state.apiUrl + '/hsdata/properties/contacts')
            .then(resp => { this.contactHSProps = resp.data; })
            .catch(e => { console.log(e); });
        const companyPropsPromise = axios.get(this.$store.state.apiUrl + '/hsdata/properties/companies')
            .then(resp => { this.companyHSProps = resp.data; })
            .catch(e => { console.log(e); });

        let pdfFields: any[] = [];
        const fieldsPromise = axios.get(this.$store.state.apiUrl + '/filler/template/' + encodeURIComponent(this.name) + '/fields')
            .then(resp => { pdfFields = resp.data.fields; })
            .catch(e => {
                if(e.response && e.response.data === 'FILE_NOT_FOUND') {
                    this.loadError = 'The uploaded PDF for this template could not be found. Please re-upload it from the templates list.';
                } else if(e.response && e.response.data === 'ENCRYPTED_PDF') {
                    this.loadError = 'This PDF is encrypted/password-protected, so its form fields can\'t be read. Try re-exporting or re-saving it without a security/permissions restriction, then re-upload it.';
                } else {
                    this.loadError = 'There was an error reading this PDF\'s form fields.';
                }
            });

        let storedFields: any[] = [];
        const storedPromise = axios.get(this.$store.state.apiUrl + '/filler/templates/' + encodeURIComponent(this.name))
            .then(resp => { storedFields = resp.data.fields || []; })
            .catch(e => { console.log(e); });

        await Promise.all([propsPromise, companyPropsPromise, fieldsPromise, storedPromise]);

        // The fields fetch failing outright (loadError set above) used to
        // fall straight through to here with pdfFields still [] - every
        // previously-saved mapping then got marked "orphaned" below, which
        // reads as "these fields don't exist any more" when the real story
        // is "we couldn't read the PDF at all right now". Stop here instead
        // and let the banner above be the only thing shown.
        if(this.loadError) {
            this.loading = false;
            this.$store.state.routeLoaded = true;
            return;
        }

        const rows: MapperRow[] = pdfFields.map(f => {
            const stored = storedFields.find((s: any) => decodeHtmlEntities(s.FormField) === decodeHtmlEntities(f.name));
            return {
                formField: f.name,
                pdfType: f.type,
                options: f.options,
                selected: stored ? `${stored.Type}::${stored.HSProp}` : '',
                orphaned: false
            };
        });

        // Stored mappings whose FormField isn't in the current PDF at all
        // (e.g. hand-typed drift from the old manual-entry UI) - never
        // silently drop these, since Save fully replaces the mapping set.
        storedFields.forEach((s: any) => {
            if(!pdfFields.some(f => decodeHtmlEntities(f.name) === decodeHtmlEntities(s.FormField))) {
                rows.push({
                    formField: s.FormField,
                    pdfType: '',
                    selected: `${s.Type}::${s.HSProp}`,
                    orphaned: true
                });
            }
        });

        this.rows = rows;
        this.loading = false;
        this.$store.state.routeLoaded = true;
    },
    methods: {
        decodeHtmlEntities,
        removeRow(i: number) {
            this.rows.splice(i, 1);
        },
        save() {
            if(this.saving) return;
            this.saving = true;

            const contactProps: any[] = [];
            const companyProps: any[] = [];
            this.rows.forEach(row => {
                if(!row.selected) return;
                const sepIndex = row.selected.indexOf('::');
                const type = row.selected.substring(0, sepIndex);
                const hsProp = row.selected.substring(sepIndex + 2);
                if(type === 'CONTACT') {
                    contactProps.push({ formField: row.formField, hsProp });
                } else {
                    companyProps.push({ formField: row.formField, hsProp });
                }
            });

            const formData = new FormData();
            formData.set('name', this.name);
            formData.set('contactProps', JSON.stringify(contactProps));
            formData.set('companyProps', JSON.stringify(companyProps));
            formData.set('isNew', 'false');
            // Deliberately no 'file' part - this is a metadata-only update,
            // the existing PDF on disk is left untouched.
            axios.post(this.$store.state.apiUrl + '/filler/template', formData)
                .then(() => {
                    alert('Mapping saved!');
                    this.saving = false;
                })
                .catch(e => {
                    alert(e.response ? e.response.data : 'There was an error.');
                    this.saving = false;
                });
        }
    }
})
